import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import useCart from 'src/hooks/use-cart';
import { Offer } from 'src/components/special-card/offer';

export const EligibleOffers = observer((): JSX.Element | null => {
  const { discountGamificationDetails, dispensary } = useCart();
  const hideDiscounts = useHideDiscountFromConsumer(dispensary);

  const flags = useFlags();
  const isGamificationFlagEnabled = flags['consumer.ecomm.gamification.rollout'] ?? false;

  if (!isGamificationFlagEnabled || !discountGamificationDetails || hideDiscounts) {
    return null;
  }

  // Right now, we don't support multiple thresholds per offer
  const eligibleOffers = discountGamificationDetails.filter((offer) => offer.thresholds.length === 1);

  if (!eligibleOffers.length) {
    return null;
  }

  return (
    <Wrapper data-testid='eligible-offers'>
      <Heading>Unlock more Offers</Heading>
      <Description>
        You have items in your cart that are eligible for special offers. Complete the offers below to unlock more
        savings!
      </Description>

      <Offers data-testid='eligible-offers-list'>
        {eligibleOffers.map(({ specialId, specialName, thresholds }) => (
          <Offer
            key={specialId}
            specialId={specialId}
            specialName={specialName}
            thresholdType={thresholds[0].thresholdType}
            threshold={thresholds[0].threshold}
            currentAmount={thresholds[0].currentAmount}
            cartItemIds={thresholds[0].applicableProducts}
            shouldLinkToSpecialOfferPage
          />
        ))}
      </Offers>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  background: #fff;
  margin-top: 16px;
  padding: 16px;
`;

const Heading = styled.h2`
  font-size: 16px;
  font-weight: bold;
  line-height: 1.2;
  margin-bottom: 8px;
`;

const Description = styled.p`
  color: #646d72;
  font-size: 12px;
  line-height: ${16 / 12};
  margin-bottom: 16px;
`;

const Offers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
